import {
  DataTypeConfig,
  DataTypeKey,
  DataTypeKeys,
  DataTypes,
  DataTypeValues,
  generateTypeChecks,
  getDataTypeGroup
} from '../../utils/data_types'

import { Pitch } from '../pitches/types'
import { americanFootballConfig } from './american_football'
import { australianRulesConfig } from './australian_rules'
import { canadianFootballConfig } from './canadian_football'
import { demonstrationConfig } from './demonstration'
import { rugbyLeagueConfig } from './rugby_league'
import { rugbyUnionConfig } from './rugby_union'

import { Bibs } from '../drills/types'
import { MetricTypeKeys } from '../metrics/types'
import { PitchSetupTypeGroup, pitchSetupTypes } from '../pitches/data_types'
import { UnitSystemKeys } from '../units/types'
import { DefaultAmericanFootballDrillRegion } from './american_football/types'
import { soccerConfig } from './soccer'
import { DefaultSoccerDrillRegion } from './soccer/types'

export type SportTypeFeatures = {
  positionNumbers: boolean
  noOfPlayersInStartingLineUp: number | null
  maxPlayers: number
  defaultUnitSystem: UnitSystemKeys
  // TODO: Reintroduce keyboard shortcuts
  // keyboardShortcuts: any
  targetEvents: Readonly<string[]>
  recentEventTableMetrics: Readonly<MetricTypeKeys[]>
  geniusAvailable: boolean
  scoreboard: boolean
  homeSideCheck: boolean
  bibs: Bibs
  defaultBibTeamDirection: number[]
  drillsEnabled?: boolean
  targetMetrics?: Readonly<MetricTypeKeys[]>

  enablePromoteAllDevices?: boolean
  enablePromoteAllDevicesOnPitch?: boolean

  disableAsSessionType?: boolean

  // Player-breakdown distance-band filter — only meaningful for
  // sports whose breakdown carries throw/flight metrics
  playerBreakdownDistanceBands?: boolean
}

export const defaultSportTypeFeatures: SportTypeFeatures = {
  positionNumbers: false,
  noOfPlayersInStartingLineUp: 0,
  maxPlayers: 0,
  defaultUnitSystem: 'SI',
  // TODO: Reintroduce keyboard shortcuts
  // keyboardShortcuts: {},
  targetEvents: [],
  recentEventTableMetrics: [],
  geniusAvailable: false,
  scoreboard: false,
  homeSideCheck: false,
  bibs: {},
  defaultBibTeamDirection: []
}

export const sportTypesConfig: {
  australianRules: typeof australianRulesConfig
  rugbyUnion: typeof rugbyUnionConfig
  rugbyLeague: typeof rugbyLeagueConfig
  americanFootball: typeof americanFootballConfig
  demonstration: typeof demonstrationConfig
  canadianFootball: typeof canadianFootballConfig
  soccer: typeof soccerConfig
} = {
  australianRules: australianRulesConfig,
  rugbyUnion: rugbyUnionConfig,
  rugbyLeague: rugbyLeagueConfig,
  americanFootball: americanFootballConfig,
  demonstration: demonstrationConfig,
  canadianFootball: canadianFootballConfig,
  soccer: soccerConfig
}

export type PitchRegionCorners = {
  topLeft: string | null
  topRight: string | null
  bottomRight: string | null
  bottomLeft: string | null
}

export type SportTypeProps = {
  reportMetricConfig: {
    playerPerformanceTable: {
      all: {
        metrics: {
          keys: string[]
        }
        eventMetricGroup: {
          eventTypes: string[]
          groupName: string | { en: string; fr: string }
        }
      }
    }
    playerComparisonTable: {
      pass: {
        metrics: string[][]
      }
    }
    dashboardMetrics: {
      performanceAgainstAverage: string[]
      cardGrid: string[][]
    }
    monitoringCards: {
      technicalMetrics: string[]
      physicalMetrics: string[]
    }
  }
  pitch: {
    default: Pitch | null
    defaultDoublePitch: Pitch | null
    pitchPointCount: number
    defaultDrillRegion?:
      | DefaultSoccerDrillRegion
      | DefaultAmericanFootballDrillRegion
      | {
          P1: string
          P2: string
          P3: string
          P4: string
          P5: string
          P6: string
          P7: string
          P8: string
        }
      | null
    drillsGoalsOrPosts?: {
      enabled: boolean
      dimensions?: {
        [key: string]: {
          width: number
          height: number
          depth?: number
        }
      }
    } | null
    drillSnapLines?: string[] | null
    setupTypes: PitchSetupTypeGroup
    minPadding2D: number | null
    minPadding3D: number | null
    cameraYScale: number | null
    textureCanvasScale: number | null
    tenYardMarkings?: number | null
    crossfieldLines?: Array<{
      name: string
      lower: string
      upper: string
    }> | null
    enableBench: boolean | null
    enableRotate?: boolean | null
    enabled2DCameraControls?: boolean | null

    leftTeamRegion: PitchRegionCorners
    rightTeamRegion: PitchRegionCorners
    leftAutosetupRegion: PitchRegionCorners
    rightAutosetupRegion: PitchRegionCorners
    pitchCornerKeys: PitchRegionCorners
    autosetupTagPositions: string[]
  }
  features: SportTypeFeatures
  // Marks the sport whose session downloads use the per-team tracking manifest
  // (`GET /game/session:{id}/tracking`) instead of `position_packets_parquet`.
  // When set, apps show the Tracking download surface and hide the packets
  // option; absent (AMF/CFL) ⇒ keep packets, no tracking surface.
  supportsTrackingDownload?: boolean
}

export const defaultSportTypeProps = {
  reportMetricConfig: {
    playerPerformanceTable: {
      all: {
        metrics: {
          keys: []
        },
        eventMetricGroup: {
          eventTypes: [],
          groupName: ''
        }
      }
    },
    playerComparisonTable: {
      pass: {
        metrics: [[]]
      }
    },
    dashboardMetrics: {
      performanceAgainstAverage: [],
      cardGrid: [[]]
    },
    monitoringCards: {
      technicalMetrics: [],
      physicalMetrics: []
    }
  },
  pitch: {
    default: null,
    defaultDoublePitch: null,
    pitchPointCount: 0,
    defaultDrillRegion: null,
    drillsGoalsOrPosts: null,
    drillSnapLines: null,
    setupTypes: pitchSetupTypes,
    minPadding2D: null,
    minPadding3D: null,
    cameraYScale: null,
    textureCanvasScale: null,
    tenYardMarkings: null,
    crossfieldLines: null,
    enableBench: null,
    enableRotate: null,
    enabled2DCameraControls: null,
    leftTeamRegion: {
      topLeft: null,
      topRight: null,
      bottomRight: null,
      bottomLeft: null
    },
    rightTeamRegion: {
      topLeft: null,
      topRight: null,
      bottomRight: null,
      bottomLeft: null
    },
    leftAutosetupRegion: {
      topLeft: null,
      topRight: null,
      bottomRight: null,
      bottomLeft: null
    },
    rightAutosetupRegion: {
      topLeft: null,
      topRight: null,
      bottomRight: null,
      bottomLeft: null
    },
    pitchCornerKeys: {
      topLeft: null,
      topRight: null,
      bottomRight: null,
      bottomLeft: null
    },
    autosetupTagPositions: []
  },
  features: defaultSportTypeFeatures
  // Placeholder props for the "unknown" sport sentinel.
} satisfies SportTypeProps

export type SportTypes = DataTypes<typeof sportTypesConfig>

export type SportTypeKey = DataTypeKey<SportTypes>

export type SportTypeKeys = DataTypeKeys<SportTypeKey>

export type SportTypeValues = DataTypeValues<SportTypeKey>

//TODO: find a better way to fit this in with the type utils
export const SPORTS = [
  'AmericanFootball',
  'AustralianRules',
  'CanadianFootball',
  'Soccer',
  'RugbyUnion',
  'RugbyLeague',
  'Demonstration'
] as const

export const getSportTypeGroup = (items: {
  [key in SportTypeKeys]: SportTypes[key]
}) => {
  return getDataTypeGroup<
    SportTypeKeys,
    SportTypeValues,
    SportTypes,
    SportTypeProps
  >(items, defaultSportTypeProps)
}

export type SportTypeGroup = ReturnType<typeof getSportTypeGroup>

export type SportTypeConfig = DataTypeConfig<
  SportTypeKeys,
  SportTypeValues,
  SportTypeProps
>

export type Sport = SportTypeGroup['items'][SportTypeKeys]

export const sportTypes = getSportTypeGroup(sportTypesConfig)

export const isSportType = generateTypeChecks<SportTypeKeys, SportTypeGroup>(
  sportTypes
)
